import { useCallback, useId, useRef, useState } from 'react'

import { PanelResizeHandle } from '@/components/layout/panel-resize-handle'
import { ActivityTimeline } from '@/components/timeline/activity-timeline'
import { useViewportSize } from '@/hooks/use-viewport-size'
import {
  resolveTimelinePanelHeight,
  TIMELINE_PANEL_KEY_STEP_PX,
  timelinePanelBounds,
} from '@/lib/timeline-panel-size'
import { selectSelectedActivity, useActivityStore } from '@/store/activity-store'

/** Vertical border + padding (px) of `element` -- the part of its height its content can't use. */
function verticalFrame(element: HTMLElement): number {
  const style = getComputedStyle(element)
  return [style.borderTopWidth, style.borderBottomWidth, style.paddingTop, style.paddingBottom]
    .map((v) => Number.parseFloat(v) || 0)
    .reduce((sum, v) => sum + v, 0)
}

/**
 * Bottom panel: the multi-metric timeline for whichever activity is
 * selected, or a neutral empty state when nothing is loaded yet.
 *
 * Resizable via the splitter on its top edge (drag, or keyboard; persisted
 * as `ui.timelineHeightPx`); the workspace above shrinks/grows to match.
 * Without a user-set height it uses the layout default, grown in stacked
 * mode so every metric panel gets a readable plot height -- see
 * `resolveTimelinePanelHeight` for the rules and viewport clamping, which
 * is re-evaluated on every window resize.
 */
export function BottomPanel() {
  const selected = useActivityStore(selectSelectedActivity)
  const layout = useActivityStore((s) => s.ui.timelineLayout ?? 'combined')
  const userHeightPx = useActivityStore((s) => s.ui.timelineHeightPx)
  const setTimelineHeightPx = useActivityStore((s) => s.setTimelineHeightPx)
  const viewport = useViewportSize()
  const panelId = useId()
  const footerRef = useRef<HTMLElement | null>(null)

  // What the stacked chart asks for (its own content height plus this
  // panel's border/padding), reported by the timeline once measured.
  const [preferredStackedHeightPx, setPreferredStackedHeightPx] = useState<number | null>(null)
  const handlePreferredHeight = useCallback((contentHeightPx: number | null) => {
    const footer = footerRef.current
    setPreferredStackedHeightPx(
      contentHeightPx == null || footer == null
        ? null
        : Math.ceil(contentHeightPx + verticalFrame(footer)),
    )
  }, [])

  const bounds = timelinePanelBounds(viewport.height)
  const height = resolveTimelinePanelHeight({
    userHeightPx,
    layout,
    preferredStackedHeightPx: selected != null ? preferredStackedHeightPx : null,
    viewportWidth: viewport.width,
    viewportHeight: viewport.height,
  })

  return (
    <footer
      ref={footerRef}
      id={panelId}
      aria-label="Timeline"
      style={{ height }}
      className="border-border bg-card relative shrink-0 border-t px-4 py-2"
    >
      <PanelResizeHandle
        label="Resize timeline"
        controls={panelId}
        value={height}
        min={bounds.min}
        max={bounds.max}
        step={TIMELINE_PANEL_KEY_STEP_PX}
        onResize={setTimelineHeightPx}
        onReset={() => setTimelineHeightPx(undefined)}
        // Straddles the top border, overlapping the workspace edge by half.
        className="absolute inset-x-0 -top-1.5 z-10"
      />
      {selected != null ? (
        <ActivityTimeline
          key={selected.id}
          loaded={selected}
          onPreferredHeightChange={handlePreferredHeight}
        />
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-0.5">
          <p className="text-card-foreground text-sm font-medium">Timeline</p>
          <p className="text-muted-foreground text-sm">
            Select or import an activity to see its metrics here.
          </p>
        </div>
      )}
    </footer>
  )
}
